import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Gift, Copy, Check, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReferralCard() {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    axios.get(`${API}/me/referral`).then(r => setData(r.data)).catch(() => setData(null));
  }, []);

  if (!data) return null;

  const link = `${window.location.origin}/?ref=${data.code}`;

  function copyLink() {
    navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success(t("link_copied") || "Referral link copied");
    setTimeout(() => setCopied(false), 1800);
  }

  async function share() {
    const shareData = {
      title: t("share_title") || "Forex Signal Pro",
      text: t("share_text") || "Join me on Forex Signal Pro and get 30 extra days free.",
      url: link,
    };
    if (navigator.share) {
      try { await navigator.share(shareData); } catch { /* user cancelled */ }
    } else {
      copyLink();
    }
  }

  return (
    <div data-testid="referral-card" className="rounded-xl border border-emerald-500/20 bg-gradient-to-br from-emerald-500/10 to-transparent p-4">
      <div className="flex items-center gap-2 text-emerald-300 font-semibold text-sm">
        <Gift className="w-4 h-4" />
        {t("refer_earn") || "Refer & earn 30 days"}
      </div>
      <p className="text-xs text-zinc-400 mt-1 leading-relaxed">
        {t("refer_desc") || "Share your link. Both of you get 30 extra free days when a friend signs up."}
      </p>
      <div className="mt-3 flex items-stretch gap-2">
        <div dir="ltr" className="flex-1 min-w-0 rounded-md bg-zinc-900 border border-zinc-800 px-3 py-2 text-xs text-zinc-300 truncate font-mono">
          {link}
        </div>
        <Button
          data-testid="copy-referral-link"
          onClick={copyLink}
          size="sm"
          className="h-auto rounded-md bg-white text-black hover:bg-zinc-200"
        >
          {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
        </Button>
        <Button
          data-testid="share-referral"
          onClick={share}
          size="sm"
          className="h-auto rounded-md bg-emerald-500 hover:bg-emerald-400 text-black"
        >
          {t("share") || "Share"}
        </Button>
      </div>
      <div className="mt-3 flex items-center gap-4 text-xs text-zinc-500">
        <div className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {data.count} {t("invited") || "invited"}</div>
        <div className="text-emerald-400">+{data.bonus_days_earned} {t("days_earned") || "days earned"}</div>
      </div>
    </div>
  );
}
